import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { devWarning } from 'helpers/logger';
import { map, pipe } from 'helpers/ramda';
import {
  calcPrecision,
  convertPriceToFixed,
  mapLimitPricetoPrice,
} from 'reducers/helpers/trade';
import { basketOrderOrdersSelector } from 'selectors/basketOrdersSelectors';
import { productsSelector } from 'selectors/tradeSelectors';
import StoreProxy from 'storeProxy';
import type { OrderBook } from 'types/publicSocket';

import type {
  BasketOrderOrderbookStore,
  BasketOrderbookItem,
} from './basketOrderBook.types';

const convertLimitPriceToPrice = (data: OrderBook, precision: number) =>
  pipe(map(mapLimitPricetoPrice), convertPriceToFixed(precision))(data);

const useBasketOrderOrderbookStoreBase = create<BasketOrderOrderbookStore>()(
  devtools(
    set => ({
      // #region States
      state: {
        buy: [],
        sell: [],
        product_id: undefined,
        symbol: undefined,
      },

      // #endregion

      // #region Actions
      actions: {
        updateBasketOrderOrderbookStore: message => {
          const { b: buy, a: sell, i: productId, s: symbol } = message;

          const reduxState = StoreProxy.getState();
          const basketOrders = basketOrderOrdersSelector(reduxState);

          // Check if product is in basket
          if (!basketOrders[productId]) {
            return;
          }

          const products = productsSelector(reduxState);
          const product = products[productId];

          // Guard against missing product data
          if (!product) {
            devWarning(
              false,
              'BasketOrderOrderbookStore',
              `Product ${productId} not found in products selector`
            );
            return;
          }

          const tickSize = product.tick_size || 0.5;
          const precision = calcPrecision(tickSize);

          const sellBook = convertLimitPriceToPrice(
            sell,
            precision
          ) as BasketOrderbookItem[];
          const buyBook = convertLimitPriceToPrice(
            buy,
            precision
          ) as BasketOrderbookItem[];

          set(
            state => ({
              ...state,
              state: {
                buy: buyBook,
                sell: sellBook,
                product_id: productId,
                symbol,
              },
            }),
            false,
            'updateBasketOrderOrderbookStore'
          );
        },

        resetBasketOrderOrderbook: () => {
          set(
            state => ({
              ...state,
              state: {
                buy: [],
                sell: [],
                product_id: undefined,
                symbol: undefined,
              },
            }),
            false,
            'resetBasketOrderOrderbook'
          );
        },
      },

      // #endregion
    }),
    {
      name: 'BasketOrderOrderbookStore',
    }
  )
);

export { useBasketOrderOrderbookStoreBase };
